/**
 * Settings Hooks
 * TanStack Query hooks for office settings, providers, and subscriptions
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type {
  DentalOffice,
  DentalOfficeProvider,
  ApiResponse,
} from '../types';

// Query keys
export const settingsKeys = {
  all: ['settings'] as const,
  office: (officeId: number) => [...settingsKeys.all, 'office', officeId] as const,
  providers: (officeId: number) => [...settingsKeys.all, 'providers', officeId] as const,
};

// Types
export interface UpdateOfficeData {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  business_hours?: string;
  timezone?: string;
  language?: string;
  currency?: string;
}

export interface CreateProviderData {
  name: string;
  email: string;
  password: string;
  role: 'dentist' | 'hygienist' | 'admin' | 'receptionist' | 'assistant';
  can_view_records?: boolean;
  can_edit_records?: boolean;
  can_schedule?: boolean;
  can_billing?: boolean;
}

export interface UpdateProviderData {
  name?: string;
  email?: string;
  password?: string;
  role?: 'dentist' | 'hygienist' | 'admin' | 'receptionist' | 'assistant';
  can_view_records?: boolean;
  can_edit_records?: boolean;
  can_schedule?: boolean;
  can_billing?: boolean;
  is_active?: boolean;
}

export interface UpdateSubscriptionData {
  subscription_tier?: 'starter' | 'professional' | 'enterprise';
  billing_cycle?: 'monthly' | 'annual';
}

// ============================================================================
// GET OFFICE DETAILS
// ============================================================================

export function useOfficeQuery(officeId: number) {
  const { token } = useAuth();

  return useQuery({
    queryKey: settingsKeys.office(officeId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<DentalOffice>>(
        `/offices/${officeId}`,
        token || undefined
      );
      return response.data;
    },
    enabled: !!token && !!officeId,
  });
}

// ============================================================================
// GET PROVIDERS
// ============================================================================

export function useProvidersQuery(officeId: number) {
  const { token } = useAuth();

  return useQuery({
    queryKey: settingsKeys.providers(officeId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<DentalOfficeProvider[]>>(
        `/offices/${officeId}/providers`,
        token || undefined
      );
      return response.data;
    },
    enabled: !!token && !!officeId,
  });
}

// ============================================================================
// UPDATE OFFICE
// ============================================================================

export async function updateOffice(
  officeId: number,
  data: UpdateOfficeData,
  token: string
): Promise<DentalOffice> {
  const response = await api.put<ApiResponse<DentalOffice>>(
    `/offices/${officeId}`,
    data,
    token
  );
  return response.data;
}

// ============================================================================
// CREATE PROVIDER
// ============================================================================

export async function createProvider(
  officeId: number,
  data: CreateProviderData,
  token: string
): Promise<DentalOfficeProvider> {
  const response = await api.post<ApiResponse<DentalOfficeProvider>>(
    `/offices/${officeId}/providers`,
    data,
    token
  );
  return response.data;
}

// ============================================================================
// UPDATE PROVIDER
// ============================================================================

export async function updateProvider(
  officeId: number,
  providerId: number,
  data: UpdateProviderData,
  token: string
): Promise<DentalOfficeProvider> {
  const response = await api.put<ApiResponse<DentalOfficeProvider>>(
    `/offices/${officeId}/providers/${providerId}`,
    data,
    token
  );
  return response.data;
}

// ============================================================================
// UPDATE SUBSCRIPTION
// ============================================================================

export async function updateSubscription(
  officeId: number,
  data: UpdateSubscriptionData,
  token: string
): Promise<DentalOffice> {
  const response = await api.put<ApiResponse<DentalOffice>>(
    `/offices/${officeId}/subscription`,
    data,
    token
  );
  return response.data;
}

// ============================================================================
// GENERATE PASSWORD
// ============================================================================

export function generatePassword(length: number = 16): string {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    password += charset[array[i] % charset.length];
  }
  return password;
}
